/**
 * Compares the operational CPT-table values (start date, end date, auth #,
 * total visits) against the values the auth-letter agent extracted and that
 * are surfaced on the Authorization Details screen (`auth_letter.*`).
 *
 * @module authLetterFieldComparison
 */
import moment from "moment";
import {
  AuthLetterMismatch,
  CptCodesTableRowModel,
} from "../../../../../data-model/cptCodesTableModel";
import {
  AuthLetterAgentInfo,
  JCodeDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { AuthLetterReviewStatus } from "../../../../../enums/authLetterReviewStatus";
import { checkAuthLetterUploadStatus } from "./checkAuthLetterUploadStatus";

/**
 * Human-readable labels for the fields compared between the CPT table and the
 * Authorization Details screen. Rendered inside the mismatch tooltip.
 */
const AUTH_LETTER_COMPARISON_LABELS = {
  startDate: "Start Date",
  endDate: "End Date",
  authNumber: "Auth #",
  totalVisits: "Total Visits",
} as const;

/** Date formats accepted across the operational table and agent payloads. */
const DATE_PARSE_FORMATS = [
  "YYYY-MM-DD",
  "MM/DD/YYYY",
  "MM-DD-YYYY",
  moment.ISO_8601,
];

/**
 * Operational (CPT-table side) values for a single J-code row.
 */
interface OperationalAuthValues {
  startDate?: string | null;
  endDate?: string | null;
  authNumber?: string | null;
  totalVisits?: string | number | null;
}

/**
 * Normalizes a date-like value to `YYYY-MM-DD` for comparison. Falls back to a
 * trimmed, lower-cased string when the value cannot be parsed as a date.
 *
 * @input value - Raw date string from either the table or the auth letter
 * @return string: Canonical comparable form, or empty string when absent
 */
const normalizeDate = (value: string | null | undefined): string => {
  if (value == null) return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  const parsed = moment(trimmed, DATE_PARSE_FORMATS, false);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : trimmed.toLowerCase();
};

/**
 * Formats an agent value for human-readable display in the mismatch tooltip.
 * Dates are shown as `MM/DD/YYYY` to match the CPT table; other values are
 * shown as trimmed strings.
 *
 * @input value - Raw agent value
 * @input isDate - Whether the value should be rendered as a date
 * @return string: Display string, or an em dash when absent
 */
const formatAgentValueForDisplay = (
  value: string | number | null | undefined,
  isDate: boolean,
): string => {
  if (value == null) return "—";
  const trimmed = String(value).trim();
  if (!trimmed) return "—";
  if (!isDate) return trimmed;
  const parsed = moment(trimmed, DATE_PARSE_FORMATS, false);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : trimmed;
};

/**
 * Normalizes a free-text value (e.g. auth number) for comparison.
 *
 * @input value - Raw text value
 * @return string: Trimmed, lower-cased form, or empty string when absent
 */
const normalizeText = (value: string | null | undefined): string => {
  if (value == null) return "";
  return String(value).trim().toLowerCase();
};

/**
 * Normalizes a visit-count value to a comparable string. Numeric inputs are
 * coerced so `"999"`, `999` and `" 999 "` compare equal.
 *
 * @input value - Raw visit count from the table or auth letter
 * @return string: Canonical numeric string, or empty string when absent
 */
const normalizeVisits = (value: string | number | null | undefined): string => {
  if (value == null) return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? trimmed.toLowerCase() : String(parsed);
};

/**
 * Whether an actual authorization letter is available for this J-code — i.e.
 * there is something on the Authorization Details screen to compare against.
 *
 * @input jCode - J-code row from auth-on-file entries
 * @return boolean: True when the row has an uploaded authorization letter
 */
const hasAuthLetterPresent = (jCode: JCodeDetails): boolean => {
  const hasUploadedLetter = checkAuthLetterUploadStatus(
    jCode.auth_status,
    jCode.documents,
  );
  const hasReviewableStatus =
    jCode.auth_letter_review_status ===
      AuthLetterReviewStatus.NewLetterUploaded ||
    jCode.auth_letter_review_status === AuthLetterReviewStatus.ReviewedAndSaved;

  return hasUploadedLetter && hasReviewableStatus;
};

/**
 * Compares operational values against the agent's auth-letter values and
 * returns the fields that differ.
 *
 * @input operational - Values currently shown in the CPT table
 * @input agent - Auth-letter values shown on the Authorization Details screen
 * @return AuthLetterMismatch[]: Mismatched fields with the agent's value for reference
 */
const getAuthLetterFieldMismatches = (
  operational: OperationalAuthValues,
  agent: AuthLetterAgentInfo | null | undefined,
): AuthLetterMismatch[] => {
  if (!agent) return [];
  const mismatches: AuthLetterMismatch[] = [];

  const compareField = (
    label: string,
    operationalValue: string,
    agentValue: string,
    agentDisplayValue: string,
  ): void => {
    if (!agentValue) return;
    if (operationalValue !== agentValue) {
      mismatches.push({ field: label, agentValue: agentDisplayValue });
    }
  };

  compareField(
    AUTH_LETTER_COMPARISON_LABELS.startDate,
    normalizeDate(operational.startDate),
    normalizeDate(agent.auth_letter_start_date),
    formatAgentValueForDisplay(agent.auth_letter_start_date, true),
  );
  compareField(
    AUTH_LETTER_COMPARISON_LABELS.endDate,
    normalizeDate(operational.endDate),
    normalizeDate(agent.auth_letter_end_date),
    formatAgentValueForDisplay(agent.auth_letter_end_date, true),
  );
  compareField(
    AUTH_LETTER_COMPARISON_LABELS.authNumber,
    normalizeText(operational.authNumber),
    normalizeText(agent.auth_letter_auth_number),
    formatAgentValueForDisplay(agent.auth_letter_auth_number, false),
  );
  compareField(
    AUTH_LETTER_COMPARISON_LABELS.totalVisits,
    normalizeVisits(operational.totalVisits),
    normalizeVisits(agent.calculated_visits),
    formatAgentValueForDisplay(agent.calculated_visits, false),
  );

  return mismatches;
};

/**
 * Computes auth-letter mismatches for a live CPT-table row (reacts to edits).
 *
 * @input row - Current CPT-table row model
 * @input jCode - Source J-code carrying the agent `auth_letter` values
 * @return AuthLetterMismatch[]: Mismatched fields (empty when none / no letter)
 */
export const getAuthLetterMismatchesForRow = (
  row: CptCodesTableRowModel,
  jCode: JCodeDetails | undefined,
): AuthLetterMismatch[] => {
  if (!jCode || !hasAuthLetterPresent(jCode)) return [];
  return getAuthLetterFieldMismatches(
    {
      startDate: row.startFrom?.data,
      endDate: row.endAt?.data,
      authNumber: row.authorizationNumber?.data,
      totalVisits: row.visits?.data,
    },
    jCode.auth_letter,
  );
};

/**
 * Computes auth-letter mismatches from a persisted J-code's own top-level
 * fields (used by the mark-as-completed guard, which reads Postgres data).
 *
 * @input jCode - J-code carrying both operational fields and `auth_letter`
 * @return AuthLetterMismatch[]: Mismatched fields (empty when none / no letter)
 */
export const getAuthLetterMismatchesForJCode = (
  jCode: JCodeDetails,
): AuthLetterMismatch[] => {
  if (!hasAuthLetterPresent(jCode)) return [];
  return getAuthLetterFieldMismatches(
    {
      startDate: jCode.start_date,
      endDate: jCode.end_date,
      authNumber: jCode.auth_number,
      totalVisits: jCode.num_visits,
    },
    jCode.auth_letter,
  );
};
